import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../utils/api';
// Phase 7 sign-off: profile email.
import { MAX_LENGTHS, useCappedPaste } from '../utils/inputValidation';
import { isFieldOk, parseServerInputError } from '../utils/inputFormHelpers';
import '../styles/authCard.css';

export default function ForgotPasswordPage() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [touched, setTouched] = useState(false);
  const onEmailPaste = useCappedPaste(MAX_LENGTHS.email);

  // 60s cooldown for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // Phase 7: client-side advisory check; backend remains the source of truth.
  const emailResult = isFieldOk(email, 'email');
  const validEmail = emailResult.ok;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!validEmail || loading) return;
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
        skipAuth: true,
      });
      if (res.status === 429) {
        setError(isKo
          ? '잠시 후 다시 시도해주세요. (15분에 3회 제한)'
          : 'Please try again later. (Limit: 3 requests per 15 minutes)');
        return;
      }
      if (!res.ok) {
        const parsed = await parseServerInputError(res, {
          fallback: isKo
            ? '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
            : 'A temporary error occurred. Please try again shortly.',
        });
        // Preserve the existing rate-limit copy verbatim.
        if (parsed.errorCode === 'rate_limit_exceeded') {
          setError(isKo
            ? '잠시 후 다시 시도해주세요. (15분에 3회 제한)'
            : 'Please try again later. (Limit: 3 requests per 15 minutes)');
          return;
        }
        setError(parsed.message);
        return;
      }
      setSubmitted(true);
      setCooldown(60);
    } catch (err) {
      setError(isKo
        ? '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        : 'A temporary error occurred. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || loading) return;
    await submit();
  };

  return (
    <div className="vp-auth-page">
      <Link to="/" className="vp-auth-logo" aria-label="VeriPass">VeriPass</Link>

      <div className="vp-auth-card">
        <h1 className="vp-auth-h1">
          {isKo ? '비밀번호 재설정' : 'Reset your password'}
        </h1>
        <p className="vp-auth-sub">
          {isKo
            ? '가입 시 사용한 이메일을 입력하시면\n비밀번호 재설정 링크를 보내드립니다.'
            : 'Enter the email you used to sign up.\nWe will send you a reset link.'}
        </p>

        {!submitted && (
          <form onSubmit={submit} noValidate>
            <div className="vp-form-row">
              <input
                type="email"
                className={`vp-input ${error ? 'is-error' : ''}`}
                placeholder={isKo ? '이메일 주소' : 'Email address'}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                onBlur={() => setTouched(true)}
                onPaste={onEmailPaste}
                maxLength={MAX_LENGTHS.email}
                disabled={loading}
                autoFocus
                autoComplete="email"
              />
              {touched && email.length > 0 && !emailResult.ok && (
                <p style={{ fontSize: 11, color: '#B91C1C', marginTop: 4 }}>{emailResult.message}</p>
              )}
            </div>

            {error && <div className="vp-status error">{error}</div>}

            <button
              type="submit"
              className="vp-btn-primary"
              disabled={!validEmail || loading}
            >
              {loading
                ? <><span className="vp-spinner" />{isKo ? '처리 중...' : 'Sending...'}</>
                : (isKo ? '재설정 링크 보내기' : 'Send reset link')}
            </button>
          </form>
        )}

        {submitted && (
          <>
            <div className="vp-status success">
              {isKo
                ? <>✓ 이메일을 확인해주세요<br />
                    <span style={{ fontSize: 12, color: '#15803D' }}>
                      {email}로 재설정 링크를 발송했습니다. 스팸함도 확인해보세요.
                    </span>
                  </>
                : <>✓ Check your email<br />
                    <span style={{ fontSize: 12, color: '#15803D' }}>
                      We sent a reset link to {email}. Check your spam folder too.
                    </span>
                  </>}
            </div>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                type="button"
                className="vp-btn-secondary"
                onClick={resend}
                disabled={cooldown > 0 || loading}
                style={{ opacity: (cooldown > 0 || loading) ? 0.5 : 1 }}
              >
                {cooldown > 0
                  ? (isKo ? `${cooldown}초 후 재발송 가능` : `Resend in ${cooldown}s`)
                  : (isKo ? '다시 보내기' : 'Resend')}
              </button>
            </div>
          </>
        )}

        <hr className="vp-divider" />

        <div className="vp-card-footer">
          <Link to="/login" className="vp-btn-secondary">
            ← {isKo ? '로그인으로 돌아가기' : 'Back to sign in'}
          </Link>
        </div>
      </div>
    </div>
  );
}
